import { Component, effect, input, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { DyeingMachine, DyeingMachineStatus, getDyeingMachineStatus } from '@app/models/dyeing-dashboard/dyeing-machine';
import { WorkOrder } from '@app/models/order/work-order';
import { ProductionCard } from '@app/models/production/production-card';
import { WorkOrderProxyService } from '@app/proxies/order/work-order.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';

@Component({
  selector: 'app-card-machine',
  templateUrl: './card-machine.component.html',
  styleUrl: './card-machine.component.scss',
})
export class CardMachineComponent implements OnInit, OnDestroy {
  stateMachine = input.required<DyeingMachine>();
  productionCardID = input<string | null>(null);
  canRedirect = input<boolean>(false);
  shortView = input<boolean>(true);

  currentProductionCard = signal<ProductionCard | null>(null);
  currentWorkOrder = signal<WorkOrder | null>(null);

  loadInfo = signal<boolean>(false);

  // Timer state
  currentTime = signal<Date>(new Date());
  private timerInterval?: ReturnType<typeof setInterval>;

  // Computed status
  machineStatus = computed<DyeingMachineStatus>(() => {
    return getDyeingMachineStatus(this.stateMachine());
  });

  // Computed timer values
  cycleElapsedTime = computed<string>(() => {
    const cycle = this.stateMachine()?.currentDyeingCycle;
    if (!cycle?.startTime) return '';

    const start = new Date(cycle.startTime);
    const end = cycle.endTime ? new Date(cycle.endTime) : this.currentTime();

    return this.formatDuration(start, end);
  });

  waitingElapsedTime = computed<string>(() => {
    const cycle = this.stateMachine()?.currentDyeingCycle;
    if (!cycle?.endTime || cycle.removedTime) return '';

    const end = new Date(cycle.endTime);
    const now = this.currentTime();

    return this.formatDuration(end, now);
  });

  showCycleTimer = computed<boolean>(() => {
    const cycle = this.stateMachine()?.currentDyeingCycle;
    return !!cycle?.startTime;
  });

  showWaitingTimer = computed<boolean>(() => {
    const cycle = this.stateMachine()?.currentDyeingCycle;
    return !!cycle?.endTime && !cycle.removedTime;
  });

  isFirstRun = computed<boolean>(() => {
    return this.stateMachine()?.currentDyeingCycle?.isFirstRun ?? false;
  });

  showFirstRunBadge = computed<boolean>(() => {
    const hasActiveCycle = this.stateMachine()?.currentDyeingCycle !== undefined;
    return hasActiveCycle && this.isFirstRun();
  });

  // Status enum for template
  DyeingMachineStatus = DyeingMachineStatus;

  constructor(
    private readonly productionCardProxyService: ProductionCardsProxyService,
    private readonly router: Router,
    private readonly workOrderProxyService: WorkOrderProxyService,
  ) {
    effect(
      () => {
        const pcID = this.productionCardID();

        if (pcID && pcID !== '') {
          this.loadProductionCard(pcID);
        }
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  ngOnInit(): void {
    if (this.stateMachine()) {
      this.loadProductionCard(this.stateMachine()?.currentProductionCardReference || '');
    }

    // Start timer interval to update current time every second
    this.timerInterval = setInterval(() => {
      this.currentTime.set(new Date());
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  private loadProductionCard(idPC: string) {
    if (idPC && idPC !== '') {
      this.loadInfo.set(true);
      this.productionCardProxyService.getProductionCardByID(idPC).subscribe({
        next: ({ data }) => {
          if (data) {
            console.log(data);
            this.loadInfo.set(false);
            this.currentProductionCard.set(data);
            this.getWorkOrder(data.workOrderReference);
          }
        },
        error: (error) => {
          this.loadInfo.set(false);
          console.log(error);
        },
      });
    }
  }

  private getWorkOrder(idWorkOrder: string) {
    this.workOrderProxyService.getById(idWorkOrder).subscribe({
      next: ({ data }) => {
        if (data) {
          console.log(data);
          this.currentWorkOrder.set(data);
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  public redirectDashboardDyeingDetails(assetID: string) {
    if (this.canRedirect()) return;

    this.router.navigate(['/dyeing/dashboard/' + assetID]);
  }

  get clientPO(): string {
    const workOrder = this.stateMachine()?.currentWorkOrder;
    if (workOrder) {
      let clientPos = workOrder.fabrics
        .map((f) => f.garmetDetails?.map((g) => g.productOrderNumber))
        .flat()
        .filter((po) => po && po.trim() !== '');

      clientPos = Array.from(new Set(clientPos)); // Remove duplicates

      return clientPos.length > 0 ? clientPos.join(', ') : 'N/A';
    }
    return 'N/A';
  }

  private formatDuration(start: Date, end: Date): string {
    const diffMs = end.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}
